import React, { CSSProperties, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import DialogBase from '../../../base/DialogBase/DialogBase';
import styles from './EditContentSettingsDialog.module.css';
import { UserRoleEnum, useEditContentSettingsMutation, useEditContentTypeSettingsMutation } from '../../../generated/graphql';
import { useCurrentUser } from '../../../App.hooks';
import JsonSettingsEditor from '../../main/JsonSettingsEditor/JsonSettingsEditor';

type EditContentSettingsDialogProps = {
    onClose: () => void,
    contentId: string,
    currentVersionId?: string,
    typeId?: string,
    settings: string,
    visible: boolean,
    style?: CSSProperties | undefined,
    children?: ReactNode | undefined,
    isAdmin: boolean;
    readOnly?: boolean;
};

const parseSettings = (settings: string): any => {
    try {
        return settings ? JSON.parse(settings) : {};
    } catch {
        return {};
    }
};

const EditContentSettingsDialog = (props: EditContentSettingsDialogProps) => {
    const { isAdmin, readOnly = false, style, visible, contentId, currentVersionId, typeId, settings, onClose } = props;
    const dataRef = useRef<any>(parseSettings(settings));
    const [hasPendingChange, setHasPendingChange] = useState(false);
    const [validationError, setValidationError] = useState('');
    const [editContentTypeSettings, editContentTypeSettingsResult] = useEditContentTypeSettingsMutation();
    const [editContentSettings, editContentSettingsResult] = useEditContentSettingsMutation();
    const currentUser = useCurrentUser();

    const canEdit = !readOnly && (currentUser?.role === UserRoleEnum.Admin || isAdmin);
    const isTypeSettings = !!typeId;

    useEffect(() => {
        dataRef.current = parseSettings(settings);
        setHasPendingChange(false);
        setValidationError('');
    }, [settings]);

    const handlePendingChange = useCallback((hasPending: boolean) => {
        setHasPendingChange(hasPending);
        if (!hasPending) setValidationError('');
    }, []);

    const handleOkClick = async () => {
        if (!canEdit) {
            onClose();
            return;
        }

        if (hasPendingChange) {
            setValidationError('Confirm or cancel the current field edit before saving the settings.');
            return;
        }

        try {
            const result = JSON.stringify(dataRef.current);

            if (isTypeSettings) {
                const mutationResult = await editContentTypeSettings({
                    variables: {
                        contentId: contentId,
                        typeId: typeId!,
                        settings: result
                    }
                });
                if ((mutationResult.data?.Content?.setTypeSettings ?? 0) < 1) {
                    throw new Error('The Content Type was not found on the current Content version. Refresh the Content and try again.');
                }
            } else {
                await editContentSettings({
                    variables: {
                        versionId: currentVersionId,
                        settings: result
                    }
                });
            }
        } catch (error) {
            setValidationError(error instanceof Error ? error.message : 'The settings could not be saved.');
            return;
        }

        onClose();
    };

    return (
        <DialogBase
            title={`${isTypeSettings ? "Content Type Settings" : "Content Settings"}${readOnly ? ' (Read only)' : ''}`}
            type={readOnly ? "Custom" : "OkCancel"}
            visible={visible}
            onClose={onClose}
            onOkClick={handleOkClick}
            actions={readOnly ? <button className="btn btn-secondary" onClick={onClose}>Close</button> : undefined}
            validationError={validationError}
            isLoading={editContentTypeSettingsResult.loading || editContentSettingsResult.loading}
            width="640px"
            className={styles.EditContentSettingsDialog}
            style={style}
            data-testid="EditContentSettingsDialog">
            <JsonSettingsEditor
                visualizeOnly={readOnly || !canEdit}
                editable={canEdit}
                style={{ minHeight: '200px', maxHeight: '500px' }}
                settings={settings}
                onPendingChange={handlePendingChange}
                onDataChange={(data) => { dataRef.current = data; }} />
        </DialogBase>
    );
};

export default EditContentSettingsDialog;
